import React from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { Booking } from '../../types/booking';
interface CoordinationProps {
  startDate?: string;
  existingBookings?: Booking[];
}
export function Coordination({
  startDate,
  existingBookings = []
}: CoordinationProps) {
  // Find who has the machine before the selected start date
  const getCurrentHolder = () => {
    if (!startDate || existingBookings.length === 0) return null;
    // Convert YYYY-MM-DD to timestamp for comparison
    const selectedStartTime = new Date(startDate).getTime();
    // Find the booking that ends closest to (but before) the selected start date
    const previousBooking = existingBookings.filter(booking => {
      // Skip completed bookings
      if (booking.status === 'completed') return false;
      // Convert DD/MM/YYYY to YYYY-MM-DD
      const [day, month, year] = booking.endDate.split('/');
      const bookingEndDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      const bookingEndTime = new Date(bookingEndDate).getTime();
      // Find bookings that end before the selected start date
      return bookingEndTime < selectedStartTime;
    }).sort((a, b) => {
      // Sort by end date descending (most recent first)
      const [dayA, monthA, yearA] = a.endDate.split('/');
      const [dayB, monthB, yearB] = b.endDate.split('/');
      const dateA = new Date(`${yearA}-${monthA.padStart(2, '0')}-${dayA.padStart(2, '0')}`).getTime();
      const dateB = new Date(`${yearB}-${monthB.padStart(2, '0')}-${dayB.padStart(2, '0')}`).getTime();
      return dateB - dateA;
    })[0];
    return previousBooking || null;
  };
  const currentHolder = getCurrentHolder();
  if (!currentHolder) {
    return <div className="space-y-6" data-id="element-489">
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700" data-id="element-490">
          <p className="font-medium" data-id="element-491">Machine at WMF</p>
          <p className="opacity-80 mt-1" data-id="element-492">
            The machine is currently at Stanthorpe Waste Management Facility. No
            coordination needed - just pick it up at your scheduled time.
          </p>
        </div>
      </div>;
  }
  // Generate avatar seed from name
  const avatarSeed = currentHolder.userName.replace(/\s+/g, '');
  return <div className="space-y-6" data-id="element-493">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6" data-id="element-494">
        <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-495">
          Current Holder
        </h4>

        <div className="flex items-center gap-4 mb-6" data-id="element-496">
          <div className="h-12 w-12 rounded-full bg-slate-200 overflow-hidden border border-slate-300" data-id="element-497">
            <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${avatarSeed}`} alt={currentHolder.userName} className="w-full h-full object-cover" data-id="element-498" />
          </div>
          <div data-id="element-499">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-500">
              {currentHolder.userName}
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-501">
              {currentHolder.location || currentHolder.farm || 'Farm'}
            </p>
          </div>
        </div>

        <div className="space-y-3" data-id="element-502">
          {currentHolder.phone && <div className="flex items-center gap-3 text-slate-700" data-id="element-503">
              <Phone size={18} className="text-slate-400" data-id="element-504" />
              <span data-id="element-505">{currentHolder.phone}</span>
            </div>}
          {currentHolder.email && <div className="flex items-center gap-3 text-slate-700" data-id="element-506">
              <Mail size={18} className="text-slate-400" data-id="element-507" />
              <span data-id="element-508">{currentHolder.email}</span>
            </div>}
          <div className="flex items-center gap-3 text-slate-700" data-id="element-509">
            <MapPin size={18} className="text-slate-400" data-id="element-510" />
            <span data-id="element-511">
              {currentHolder.location || currentHolder.farm || 'Farm'}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-sm text-amber-800" data-id="element-512">
        <p className="font-medium mb-1" data-id="element-513">Coordination Required</p>
        <p className="opacity-90" data-id="element-514">
          Please contact {currentHolder.userName.split(' ')[0]} to arrange the
          specific pickup time and details.
        </p>
      </div>

      <div className="flex flex-col gap-3" data-id="element-515">
        {currentHolder.email && <a href={`mailto:${currentHolder.email}?subject=Machine Pickup Coordination&body=Hi ${currentHolder.userName.split(' ')[0]},%0D%0A%0D%0AI would like to coordinate the pickup of the plastic collection machine.%0D%0A%0D%0AThank you`} className="w-full py-2.5 px-4 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-516">
            <Mail size={16} data-id="element-517" />
            Send Email
          </a>}

        {currentHolder.phone && <a href={`tel:${currentHolder.phone.replace(/\s+/g, '')}`} className="w-full py-2.5 px-4 bg-white border border-slate-300 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2" data-id="element-518">
            <Phone size={16} data-id="element-519" />
            Call {currentHolder.userName.split(' ')[0]}
          </a>}
      </div>
    </div>;
}